// File Metadata:
// Filename: DrawerNav.jsx
// Version: 2.03
// Created: 2025-03-19 23:09:16
// Last Modified: 2026-05-31
// Authors: Dieuwer Descheemaecker, GitHub Copilot, AI Assistant
// Description: "Obsidian Home" sidebar navigation with role-aware dashboard and checklist entry points

import {
  Drawer, List, ListItem, ListItemText, ListItemButton,
  Box, Typography, Divider, ListItemIcon, Avatar, Chip, Badge
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useTheme, alpha } from '@mui/material/styles';

import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import ChecklistRtlRoundedIcon from '@mui/icons-material/ChecklistRtlRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';

const DRAWER_WIDTH = 248;
const brandFont = '"Bricolage Grotesque", "Helvetica Neue", sans-serif';

const baseMenuItems = [
  { text: 'Home', path: '/', icon: <HomeRoundedIcon />, section: 'main' },
  { text: 'Chat', path: '/chat', icon: <ChatBubbleRoundedIcon />, section: 'main' },
  { text: 'Chores', path: '/chores', icon: <AssignmentRoundedIcon />, section: 'main' },
  { text: 'Checklists', path: '/checklists', icon: <ChecklistRtlRoundedIcon />, section: 'main' },
  { text: 'To-Dos', path: '/todos', icon: <TaskAltRoundedIcon />, section: 'main' },
  { text: 'Rewards', path: '/rewards', icon: <EmojiEventsRoundedIcon />, section: 'main' },
  { text: 'Game Room', path: '/games', icon: <SportsEsportsRoundedIcon />, section: 'main' },
  { text: 'Timer', path: '/timer', icon: <TimerRoundedIcon />, section: 'main' },
  { text: 'Dashboard', path: '/dashboard', icon: <ChecklistRtlRoundedIcon />, section: 'main', parentOnly: true },
  { text: 'Profile', path: '/profile', icon: <AccountCircleRoundedIcon />, section: 'account' },
  { text: 'Finance', path: '/finance', icon: <AccountBalanceWalletRoundedIcon />, section: 'account', parentOnly: true },
  { text: 'Transactions', path: '/finance/transactions', icon: <ListAltRoundedIcon />, section: 'account', parentOnly: true, subItem: true },
  { text: 'Rules', path: '/finance/rules', icon: <RuleRoundedIcon />, section: 'account', parentOnly: true, subItem: true },
  { text: 'Categories', path: '/finance/categories', icon: <LocalOfferRoundedIcon />, section: 'account', parentOnly: true, subItem: true },
  { text: 'Budgets', path: '/finance/budgets', icon: <SavingsRoundedIcon />, section: 'account', parentOnly: true, subItem: true },
  { text: 'Accounts', path: '/finance/accounts', icon: <AccountBalanceRoundedIcon />, section: 'account', parentOnly: true, subItem: true },
  { text: 'Scanner', path: '/scanner', icon: <QrCodeScannerRoundedIcon />, section: 'account' },
];

const DrawerNav = ({ open, toggleDrawer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.data);
  const unreadCountByRoom = useSelector((state) => state.chat?.unreadCountByRoom ?? {});
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [recentPages, setRecentPages] = useState([]);

  // Compute total unread messages across all rooms
  const totalUnread = useMemo(
    () => Object.values(unreadCountByRoom).reduce((sum, n) => sum + (n || 0), 0),
    [unreadCountByRoom]
  );

  const allowedMenuItems = useMemo(() => {
    const isParent = user?.role === 'parent' || user?.role === 'admin';
    return [...baseMenuItems].filter((item) => (item.parentOnly ? isParent : true));
  }, [user?.role]);

  useEffect(() => {
    const match = allowedMenuItems.find(item => item.path === location.pathname);
    if (match && location.pathname !== '/') {
      setRecentPages(prev => [
        match,
        ...prev.filter(item => item.path !== location.pathname)
      ].slice(0, 3));
    }
  }, [location.pathname, allowedMenuItems]);

  const handleNav = (path) => {
    navigate(path);
    if (window.innerWidth < 900) toggleDrawer();
  };

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', pt: '64px' }}>
      {user && (
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: '14px',
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.1 : 0.07),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          }}>
            <Avatar
              src={user.avatar}
              alt={user.name}
              sx={{
                width: 44,
                height: 44,
                bgcolor: alpha(theme.palette.primary.main, 0.25),
                color: theme.palette.primary.main,
                fontFamily: brandFont,
                fontWeight: 700,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                flexShrink: 0,
              }}
            >
              {user.name?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, lineHeight: 1.3, fontFamily: brandFont, noWrap: true }}
                noWrap
              >
                {user.name}
              </Typography>
              <Chip
                label={user.role === 'admin' ? 'Admin' : user.role === 'parent' ? 'Parent' : 'Child'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  mt: 0.3,
                  bgcolor: alpha(theme.palette.primary.main, 0.18),
                  color: theme.palette.primary.main,
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Divider sx={{ mx: 2 }} />

      <Box sx={{ px: 1, py: 1, flex: 1, overflowY: 'auto' }}>
        <Typography
          variant="overline"
          sx={{ px: 2, py: 1, display: 'block', color: 'text.disabled', fontSize: '0.65rem' }}
        >
          Navigation
        </Typography>
        <List disablePadding dense>
          {allowedMenuItems.filter(i => i.section === 'main').map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNav(item.path)}
              >
                <ListItemIcon>
                  {item.text === 'Chat' && totalUnread > 0
                    ? <Badge badgeContent={totalUnread} color="secondary" max={99}>{item.icon}</Badge>
                    : item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 1, mb: 0.5 }}>
          <Typography
            variant="overline"
            sx={{ px: 2, py: 1, display: 'block', color: 'text.disabled', fontSize: '0.65rem' }}
          >
            Account
          </Typography>
          <List disablePadding dense>
            {allowedMenuItems.filter(i => i.section === 'account').map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
                  onClick={() => handleNav(item.path)}
                  sx={item.subItem ? { pl: 3.5 } : undefined}
                >
                  <ListItemIcon sx={item.subItem ? { minWidth: 36 } : undefined}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={item.subItem ? { fontSize: '0.85rem' } : undefined}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      {recentPages.length > 0 && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 1, py: 1 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, py: 0.5, display: 'block', color: 'text.disabled', fontSize: '0.65rem' }}
            >
              Recent
            </Typography>
            <List disablePadding dense>
              {recentPages.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    dense
                    onClick={() => handleNav(item.path)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <HistoryRoundedIcon fontSize="small" sx={{ opacity: 0.5 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}

      <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.disabled">
          FamilySync · v1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={open}
        onClose={toggleDrawer}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default DrawerNav;
